import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Password as PasswordIcon,
  Schedule as ScheduleIcon,
  Login as LoginIcon,
  NetworkCheck as NetworkIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { usePermission } from '../../hooks/usePermission';

const DEFAULT_SECURITY = {
  passwordPolicy: {
    minLength: 8, requireUppercase: true, requireLowercase: true, requireNumbers: true,
    requireSpecialChars: false, maxAge: 90, preventReuse: 5
  },
  sessionPolicy: { maxDuration: 480, idleTimeout: 60, maxConcurrentSessions: 3 },
  loginPolicy: { maxFailedAttempts: 5, lockoutDuration: 30, requireMFA: false },
  ipWhitelist: [],
  ipBlacklist: []
};

const IP_PATTERN = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$|^[0-9a-fA-F:]+(\/\d{1,3})?$/;

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const toInt = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
};

const SecurityPolicies = () => {
  const { systemSettings, companyConfig, updateCompanyConfiguration, loading } = useSettings();
  const { canManageCompany } = usePermission();

  const [security, setSecurity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [ipDialogOpen, setIpDialogOpen] = useState(false);
  const [ipForm, setIpForm] = useState({ ip: '', description: '', list: 'ipWhitelist' });

  useEffect(() => {
    if (systemSettings) {
      const platform = systemSettings.security || {};
      const own = (companyConfig && companyConfig.security) || {};
      const stored = { ...platform, ...own, passwordPolicy: { ...(platform.passwordPolicy || {}), ...(own.passwordPolicy || {}) }, sessionPolicy: { ...(platform.sessionPolicy || {}), ...(own.sessionPolicy || {}) }, loginPolicy: { ...(platform.loginPolicy || {}), ...(own.loginPolicy || {}) } };
      setSecurity({
        ...DEFAULT_SECURITY,
        ...stored,
        passwordPolicy: { ...DEFAULT_SECURITY.passwordPolicy, ...(stored.passwordPolicy || {}) },
        sessionPolicy: { ...DEFAULT_SECURITY.sessionPolicy, ...(stored.sessionPolicy || {}) },
        loginPolicy: { ...DEFAULT_SECURITY.loginPolicy, ...(stored.loginPolicy || {}) },
        ipWhitelist: Array.isArray(stored.ipWhitelist) ? stored.ipWhitelist : [],
        ipBlacklist: Array.isArray(stored.ipBlacklist) ? stored.ipBlacklist : []
      });
    }
  }, [systemSettings, companyConfig]);

  const setPolicy = (policy, field, value) => {
    setSecurity(prev => ({ ...prev, [policy]: { ...prev[policy], [field]: value } }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      // Saved as this organisation's override of the platform defaults; the backend deep-merges it.
      await updateCompanyConfiguration({ security });
      setSuccess('Security policies updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(serverMessage(err, 'Failed to save security policies. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddIp = () => {
    const ip = ipForm.ip.trim();
    if (!ip) return;
    if (!IP_PATTERN.test(ip)) {
      setError('Enter a valid IPv4/IPv6 address or CIDR range (e.g. 192.168.1.0/24)');
      return;
    }
    setSecurity(prev => {
      const list = prev[ipForm.list] || [];
      if (list.some(entry => entry.ip === ip)) return prev;
      return { ...prev, [ipForm.list]: [...list, { ip, description: ipForm.description.trim(), enabled: true }] };
    });
    setIpForm({ ip: '', description: '', list: 'ipWhitelist' });
    setIpDialogOpen(false);
    setError(null);
  };

  const handleToggleIp = (listKey, index, enabled) => {
    setSecurity(prev => ({
      ...prev,
      [listKey]: prev[listKey].map((entry, i) => (i === index ? { ...entry, enabled } : entry))
    }));
  };

  const handleRemoveIp = (listKey, index) => {
    setSecurity(prev => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== index) }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!systemSettings || !security) {
    return (
      <Alert severity="warning">
        Security policies are unavailable. The system settings could not be loaded; this usually means your
        role does not include company settings access, or the settings service is unreachable. Try refreshing
        the page or contact a company administrator.
      </Alert>
    );
  }

  const renderIpList = (listKey, emptyText) => {
    const list = security[listKey] || [];
    if (list.length === 0) {
      return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
    }
    return (
      <List dense disablePadding>
        {list.map((entry, index) => (
          <ListItem key={`${listKey}-${entry.ip}-${index}`} disableGutters>
            <ListItemText
              primary={entry.ip}
              secondary={entry.description || null}
              primaryTypographyProps={{ sx: { fontFamily: 'monospace' } }}
            />
            <ListItemSecondaryAction>
              <Tooltip title={entry.enabled !== false ? 'Enabled' : 'Disabled'}>
                <Switch
                  size="small"
                  checked={entry.enabled !== false}
                  onChange={(e) => handleToggleIp(listKey, index, e.target.checked)}
                  disabled={!canManageCompany}
                />
              </Tooltip>
              <IconButton
                edge="end"
                size="small"
                onClick={() => handleRemoveIp(listKey, index)}
                disabled={!canManageCompany}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  const readOnly = !canManageCompany;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view security policies but saving changes requires the company management permission.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Password policy */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<PasswordIcon />} title="Password Policy" subheader="Requirements for user passwords" />
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                The password policy is enforced when an invited user sets up their password. Existing passwords
                and social sign-ins (Google / Microsoft) are not re-validated.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Minimum Length"
                    type="number"
                    value={security.passwordPolicy.minLength}
                    onChange={(e) => setPolicy('passwordPolicy', 'minLength', toInt(e.target.value, 8))}
                    inputProps={{ min: 6, max: 128 }}
                    disabled={readOnly}
                  />
                </Grid>
                {[
                  ['requireUppercase', 'Require Uppercase Letters'],
                  ['requireLowercase', 'Require Lowercase Letters'],
                  ['requireNumbers', 'Require Numbers'],
                  ['requireSpecialChars', 'Require Special Characters']
                ].map(([field, label]) => (
                  <Grid item xs={12} key={field}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(security.passwordPolicy[field])}
                          onChange={(e) => setPolicy('passwordPolicy', field, e.target.checked)}
                          disabled={readOnly}
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Password Max Age (days)"
                    type="number"
                    value={security.passwordPolicy.maxAge}
                    onChange={(e) => setPolicy('passwordPolicy', 'maxAge', toInt(e.target.value, 0))}
                    inputProps={{ min: 0, max: 365 }}
                    helperText="0 = never expires"
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Prevent Reuse (count)"
                    type="number"
                    value={security.passwordPolicy.preventReuse}
                    onChange={(e) => setPolicy('passwordPolicy', 'preventReuse', toInt(e.target.value, 0))}
                    inputProps={{ min: 0, max: 24 }}
                    disabled={readOnly}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Session policy */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<ScheduleIcon />} title="Session Policy" subheader="Session timeouts and limits" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Max Session Duration (minutes)"
                    type="number"
                    value={security.sessionPolicy.maxDuration}
                    onChange={(e) => setPolicy('sessionPolicy', 'maxDuration', toInt(e.target.value, 480))}
                    inputProps={{ min: 15, max: 1440 }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Idle Timeout (minutes)"
                    type="number"
                    value={security.sessionPolicy.idleTimeout}
                    onChange={(e) => setPolicy('sessionPolicy', 'idleTimeout', toInt(e.target.value, 60))}
                    inputProps={{ min: 5, max: 240 }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Max Concurrent Sessions"
                    type="number"
                    value={security.sessionPolicy.maxConcurrentSessions}
                    onChange={(e) => setPolicy('sessionPolicy', 'maxConcurrentSessions', toInt(e.target.value, 3))}
                    inputProps={{ min: 1, max: 10 }}
                    disabled={readOnly}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Login policy */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<LoginIcon />} title="Login Policy" subheader="Failed-attempt limits and MFA" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Max Failed Attempts"
                    type="number"
                    value={security.loginPolicy.maxFailedAttempts}
                    onChange={(e) => setPolicy('loginPolicy', 'maxFailedAttempts', toInt(e.target.value, 5))}
                    inputProps={{ min: 3, max: 20 }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Lockout Duration (minutes)"
                    type="number"
                    value={security.loginPolicy.lockoutDuration}
                    onChange={(e) => setPolicy('loginPolicy', 'lockoutDuration', toInt(e.target.value, 30))}
                    inputProps={{ min: 5, max: 1440 }}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(security.loginPolicy.requireMFA)}
                        onChange={(e) => setPolicy('loginPolicy', 'requireMFA', e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Require Multi-Factor Authentication"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* IP allow / block lists */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<NetworkIcon />}
              title="IP Restrictions"
              subheader="Allowed and blocked addresses or CIDR ranges"
              action={
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setIpDialogOpen(true)}
                  size="small"
                  disabled={readOnly}
                >
                  Add IP
                </Button>
              }
            />
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Whitelist (allowed)</Typography>
              <Box sx={{ mb: 2 }}>{renderIpList('ipWhitelist', 'No IPs whitelisted - all addresses allowed')}</Box>
              <Typography variant="subtitle2" gutterBottom>Blacklist (blocked)</Typography>
              <Box>{renderIpList('ipBlacklist', 'No IPs blacklisted')}</Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={readOnly ? 'Requires company management permission' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || readOnly}
              size="large"
            >
              {saving ? 'Saving...' : 'Save Security Policies'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Add IP dialog */}
      <Dialog open={ipDialogOpen} onClose={() => setIpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add IP Address</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IP Address or CIDR"
                value={ipForm.ip}
                onChange={(e) => setIpForm(prev => ({ ...prev, ip: e.target.value }))}
                placeholder="192.168.1.1 or 192.168.1.0/24"
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={ipForm.description}
                onChange={(e) => setIpForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Head office egress"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>List</InputLabel>
                <Select
                  value={ipForm.list}
                  label="List"
                  onChange={(e) => setIpForm(prev => ({ ...prev, list: e.target.value }))}
                >
                  <MenuItem value="ipWhitelist">Whitelist (allow)</MenuItem>
                  <MenuItem value="ipBlacklist">Blacklist (block)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIpDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddIp} variant="contained" disabled={!ipForm.ip.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityPolicies;
